<template>
  <div class="app-header">
    <ContainerLayout padding="0 24px">
      <div class="header-inner">
        <!-- 左侧：Logo与标题 -->
        <div class="header-left">
          <div class="logo-section">
            <img src="@/assets/owl-logo.png" alt="猫头鹰Logo" class="logo">
            <h1 v-if="showLogo || !title" class="app-title">猫头鹰</h1>
            <div v-else class="page-title-section">
              <div class="title-content">
                <Icon v-if="icon" :type="icon" :size="20" :color="iconColor" class="header-icon" />
                <h1 class="page-title">{{ title }}</h1>
              </div>
            </div>
          </div>
        </div>

        <!-- 中间：导航菜单 (Desktop) -->
        <div class="header-center">
          <NavigationMenu />
        </div>

        <!-- 右侧：用户信息与操作 -->
        <div class="header-right">
          <div v-if="$slots.extra" class="header-extra">
            <slot name="extra"></slot>
          </div>
          <UserInfo :showAvatar="true" :showDetails="!compact" :showMeta="false" :showStatus="false" :showActions="true"
            :compact="compact" :avatarSize="32" />
            
          <!-- Mobile Menu Button -->
          <Button type="text" class="mobile-menu-btn" @click.stop="showMobileMenu = true">
            <Icon type="md-menu" size="24" />
          </Button>
        </div>
      </div>
    </ContainerLayout>

    <!-- Mobile Menu Drawer -->
    <Drawer v-model="showMobileMenu" placement="left" :closable="false" class-name="mobile-menu-drawer" width="260">
      <div class="mobile-menu-header">
        <img src="@/assets/owl-logo.png" class="logo-small" />
        <span class="app-title-small">猫头鹰</span>
      </div>
      <div class="mobile-menu-content">
        <NavigationMenu mode="vertical" @item-click="showMobileMenu = false" />
      </div>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import UserInfo from '@/components/UserInfo.vue'
import ContainerLayout from '@/components/common/ContainerLayout.vue'
import NavigationMenu from '@/components/NavigationMenu.vue'

interface Props {
  showLogo?: boolean
  title?: string
  subtitle?: string
  icon?: string
  iconSize?: number
  iconColor?: string
  compact?: boolean
  customClass?: string
}

withDefaults(defineProps<Props>(), {
  showLogo: false,
  title: '',
  subtitle: '',
  icon: '',
  iconSize: 32,
  iconColor: '#667eea',
  compact: false,
  customClass: ''
})

const showMobileMenu = ref(false)
</script>

<style scoped>
.app-header {
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--glass-border);
  position: sticky;
  top: 0;
  z-index: 100;
  height: 64px;
}

.container-layout {
  height: 100%;
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  min-width: 200px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-content {
  display: flex;
  align-items: center;
}

.header-icon {
  margin-right: 8px;
}

.logo {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.app-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.page-title {
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
}

.header-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 200px;
  justify-content: flex-end;
}

.header-extra {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mobile-menu-btn {
  display: none;
  color: var(--text-primary) !important;
  padding: 0 8px;
}

.mobile-menu-btn:hover {
  color: var(--accent-primary) !important;
}

.mobile-menu-btn:active,
.mobile-menu-btn.ivu-btn-active {
  color: var(--accent-primary) !important;
}

/* Mobile Menu Styles */
.mobile-menu-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 16px;
}

.logo-small {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.app-title-small {
  font-size: 18px;
  font-weight: 700;
  color: #f8fafc;
}

@media (max-width: 768px) {
  .header-center {
    display: none; 
  }
  
  .mobile-menu-btn {
    display: block;
  }
  
  .header-left {
    min-width: 0; /* Crucial for text truncation in flex items */
    flex: 1; /* Allow left to take available space */
    margin-right: 16px; /* Add some spacing */
  }

  .logo-section {
    width: 100%; /* Ensure it fills container for truncation to work */
  }

  /* Truncate titles on mobile */
  .app-title, .page-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 16px; /* Slightly smaller font on mobile */
  }
  
  .header-right {
    min-width: auto;
    flex-shrink: 0; /* Prevent right side from shrinking */
    gap: 8px;
  }
  
  /* Hide user name on mobile to save space */
  .header-right :deep(.user-details) {
    display: none;
  }
}
</style>
